import { useEffect, useState } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Iconify from "./iconify";
import { useNavigate } from "react-router-dom";
import { createBlobInContainer } from "src/utils/azureBlob";
import { get, post, put } from "src/utils/networkUtils";
import MinUsersSelect from "./MinUsersSelect";
import MaxUsersSelect from "./MaxUsersSelect";

export default function ClubForm({
  mode = "create",
  challengeId = null,
  initialData = null,
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(
    initialData || { isAutoGeneration: true, guaranteeValues: {} }
  );
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guaranteeModalOpen, setGuaranteeModalOpen] = useState(false);
  const [guaranteeValues, setGuaranteeValues] = useState({});
  const [xpLevels, setXpLevels] = useState([]);

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";
  const isCreateMode = mode === "create";

  useEffect(() => {
    const fetchTeams = async () => {
      const response = await get("/admin/teams?competition_id=1");
      if (response.teams.length) {
        setTeams(response.teams);
      }
    };
    const fetchXpLevels = async () => {
      const response = await get("/admin/xp-level-settings");
      if (response.settings.length) {
        setXpLevels(response.settings);
      }
    };
    fetchTeams();
    fetchXpLevels();
  }, []);

  const handleInputChange = (field, value) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePayoutFormatChange = (value) => {
    if (isViewMode || !value) return;

    // Check if it's a guarantee format
    if (value.startsWith("GRT-")) {
      // Check if we can edit (not in edit mode and not in Available/Live/Completed status)
      const canEdit =
        !isViewMode &&
        (!formData.status ||
          !["open", "live", "finished"].includes(formData.status));
      if (canEdit) {
        // Pre-populate with existing values if available
        const inputCount = getGuaranteeInputCount(value);
        const existingValues = {};
        for (let i = 1; i <= inputCount; i++) {
          const key = `${value}_${i}`;
          if (formData.guaranteeValues && formData.guaranteeValues[key]) {
            existingValues[key] = formData.guaranteeValues[key];
          }
        }
        setGuaranteeValues(existingValues);

        setGuaranteeModalOpen(true);
        // Set the selected format but don't update formData yet
        setFormData((prev) => ({ ...prev, tempPayoutFormat: value }));
      } else {
        // Just update the payout format without opening modal
        setFormData((prev) => ({ ...prev, payoutFormat: value }));
      }
    } else {
      // Regular format, update normally
      setFormData((prev) => ({ ...prev, payoutFormat: value }));
    }
  };
  const handleSave = async () => {
    const apiPayload = {
      clubName: formData.clubName,
      minXpLevel: formData.minXpLevel,
      payoutFormat: formData.payoutFormat,
      prizePoolPercentage: formData.prizePoolPercentage,
      maxUsers: formData.maxUsers,
      minUsers: formData.minUsers,
      entryFee: formData.entryFee,
      isAutoGeneration: formData.isAutoGeneration,
      iconUrl: formData.iconUrl || null,
      bannerUrl: formData.bannerUrl || null,
      teamId: formData.teamId,
      guaranteeValues:
        formData.guaranteeValues &&
        Object.keys(formData.guaranteeValues).length > 0
          ? formData.guaranteeValues
          : null,
    };
    try {
      setLoading(true);
      const resp = isEditMode
        ? await put(`/admin/clubs/${initialData.clubId}`, apiPayload)
        : await post("/admin/clubs", apiPayload);
      if (resp.success) {
        setLoading(false);
        navigate("/dashboard/club");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error creating club challenge:", error);
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/club");
  };

  const handleViewAll = () => {
    navigate("/dashboard/club-challenges");
  };

  const handleFileUpload = async (type, event) => {
    if (isViewMode) return; // Prevent uploads in view mode
    const file = event.target.files[0];
    if (file) {
      // File validation
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, and WEBP formats are allowed.");
        return;
      }

      if (file.size > maxSize) {
        alert("File size should not exceed 2MB.");
        return;
      }

      // Upload to Azure
      try {
        const newFileName = file.name.replace(/\s+/g, "_");
        const renamedFile = new File([file], newFileName, {
          type: file.type,
        });

        await createBlobInContainer(renamedFile);

        const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
        const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
        const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${newFileName}`;

        // Update form data with Azure URL
        if (type === "icon") {
          setFormData((prev) => ({ ...prev, iconUrl: imageUrl }));
        } else if (type === "banner1") {
          setFormData((prev) => ({ ...prev, bannerUrl: imageUrl }));
        }

        console.log(`${type} uploaded successfully:`, imageUrl);
      } catch (error) {
        console.error(`Error uploading ${type}:`, error);
        alert(`Error uploading ${type}. Please try again.`);
      }
    }
  };

  const getChipColor = (format) => {
    return "#4CAF50";
  };

  const getChipLabel = (format) => {
    if (!format) return "";

    if (format.startsWith("GRT")) {
      if (format === "GRT-WTA") return "GRT-WTA";
      if (format === "GRT-Top3") return "GRT-Top3";
      if (format === "GRT-Top5") return "GRT-Top5";
      if (format === "GRT-Top10") return "GRT-Top10";
      return "GRT";
    }
    if (format.startsWith("Top")) {
      if (format === "Top2-80-20") return "Top2";
      if (format === "Top2-70-30") return "Top2";
      if (format === "Top3") return "Top3";
      return "Top";
    }
    if (format === "WTA") return "WTA";
    if (format === "50/50") return "50/50";
    if (format.includes("x")) return format;
    return format;
  };

  const getDescription = (format) => {
    if (!format) return "";

    switch (format) {
      case "2x":
        return "2x (45%) min 3";
      case "3x":
        return "3x (30%) min 4";
      case "4x":
        return "4x (22.5%) min 5";
      case "5x":
        return "5x (18%) min 6";
      case "10x":
        return "10x (9%) min 12";
      case "20x":
        return "20x (4.5%) min 24";
      case "WTA":
        return "WTA: 1-100% min 2";
      case "Top2-80-20":
        return "Top 2: 1-80%, 2-20% min 8";
      case "Top2-70-30":
        return "Top 2: 1-70%, 2-30% min 8";
      case "Top3":
        return "Top 3: 1-65%, 2-30%, 3-15% min 8";
      case "50/50":
        return "50/50: 50% win equal share min 4";
      case "GRT-WTA":
        return getGuaranteeDescription(format);
      case "GRT-Top3":
        return getGuaranteeDescription(format);
      case "GRT-Top5":
        return getGuaranteeDescription(format);
      case "GRT-Top10":
        return getGuaranteeDescription(format);
      default:
        return format;
    }
  };

  const renderPayoutFormat = () => {
    if (!formData.payoutFormat) {
      return (
        <Typography variant="body2" color="text.disabled">
          Select a payout format
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Chip
          label={getChipLabel(formData.payoutFormat)}
          size="small"
          sx={{
            backgroundColor: getChipColor(formData.payoutFormat),
            color: "white",
            fontWeight: 500,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          {getDescription(formData.payoutFormat)}
        </Typography>
      </Box>
    );
  };

  const getPageTitle = () => {
    if (isCreateMode) return "Club Challenges - Create New Club";
    if (isEditMode) return `Club ${challengeId} - Edit Details`;
    if (isViewMode) return `Club ${challengeId} - View Details`;
    return "Club Challenge";
  };

  const getGuaranteeDescription = (format) => {
    const inputCount = getGuaranteeInputCount(format);
    const baseDescription =
      {
        "GRT-WTA": 'GRT: Guarantee WTA "X" coins',
        "GRT-Top3": 'GRT: Guarantee Top-3 "X" coins',
        "GRT-Top5": 'GRT: Guarantee Top-5 "X" coins',
        "GRT-Top10": 'GRT: Guarantee Top-10 "X" coins',
      }[format] || format;

    // If we have guarantee values set, show them
    if (formData.guaranteeValues) {
      const values = [];
      for (let i = 1; i <= inputCount; i++) {
        const key = `${format}_${i}`;
        const value = formData.guaranteeValues[key];
        if (value) {
          values.push(`${i}:${value}`);
        }
      }
      if (values.length === inputCount) {
        return `${baseDescription} (${values.join(", ")})`;
      }
    }

    return baseDescription;
  };

  const getGuaranteeInputCount = (format) => {
    switch (format) {
      case "GRT-WTA":
        return 1;
      case "GRT-Top3":
        return 3;
      case "GRT-Top5":
        return 5;
      case "GRT-Top10":
        return 10;
      default:
        return 1;
    }
  };

  const getGuaranteeTitle = (format) => {
    switch (format) {
      case "GRT-WTA":
        return 'Guarantee WTA "X" Coins';
      case "GRT-Top3":
        return 'Guarantee Top-3 "X" Coins';
      case "GRT-Top5":
        return 'Guarantee Top-5 "X" Coins';
      case "GRT-Top10":
        return 'Guarantee Top-10 "X" Coins';
      default:
        return "Guarantee Settings";
    }
  };

  const handleGuaranteeModalSave = () => {
    const format = formData.tempPayoutFormat;
    const inputCount = getGuaranteeInputCount(format);

    // Validate that all inputs have values
    const hasAllValues = Array.from(
      { length: inputCount },
      (_, i) => i + 1
    ).every(
      (position) =>
        guaranteeValues[`${format}_${position}`] &&
        guaranteeValues[`${format}_${position}`] > 0
    );

    if (!hasAllValues) {
      alert("Please fill in all guarantee coin values.");
      return;
    }

    // Save the payout format and guarantee values
    setFormData((prev) => ({
      ...prev,
      payoutFormat: format,
      guaranteeValues: { ...prev.guaranteeValues, ...guaranteeValues },
      tempPayoutFormat: undefined,
    }));

    setGuaranteeModalOpen(false);
  };

  const handleGuaranteeModalCancel = () => {
    setGuaranteeModalOpen(false);
    setFormData((prev) => ({ ...prev, tempPayoutFormat: undefined }));
  };

  const handleGuaranteeValueChange = (key, value) => {
    setGuaranteeValues((prev) => ({
      ...prev,
      [key]: parseInt(value) || 0,
    }));
  };

  const renderGuaranteeModal = () => {
    if (!formData.tempPayoutFormat) return null;

    const format = formData.tempPayoutFormat;
    const inputCount = getGuaranteeInputCount(format);
    const title = getGuaranteeTitle(format);

    return (
      <Dialog
        open={guaranteeModalOpen}
        onClose={handleGuaranteeModalCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#667eea" }}>
            {title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {Array.from({ length: inputCount }, (_, i) => {
              const position = i + 1;
              const key = `${format}_${position}`;

              return (
                <Box
                  key={position}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, minWidth: "80px" }}
                  >
                    Position {position}:
                  </Typography>
                  <TextField
                    type="number"
                    value={guaranteeValues[key] || ""}
                    onChange={(e) =>
                      handleGuaranteeValueChange(key, e.target.value)
                    }
                    placeholder="Enter coin amount"
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <img
                          src="/assets/icons/ufcoin.svg"
                          alt="coin"
                          style={{ width: 20, height: 20, marginRight: 8 }}
                        />
                      ),
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleGuaranteeModalCancel}
            variant="outlined"
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGuaranteeModalSave}
            variant="contained"
            sx={{
              backgroundColor: "#667eea",
              "&:hover": {
                backgroundColor: "#5a6fd8",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderActionButtons = () => {
    if (isViewMode) {
      return (
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={handleViewAll}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            View all Club Challenges
          </Button>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              borderColor: "#667eea",
              color: "#667eea",
              "&:hover": {
                borderColor: "#5a6fd8",
                backgroundColor: "rgba(102, 126, 234, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            sx={{
              backgroundColor: "#667eea",
              "&:hover": {
                backgroundColor: "#5a6fd8",
              },
            }}
          >
            Save
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={2}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{ width: "150px" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ width: "150px" }}
        >
          Save
        </Button>
      </Stack>
    );
  };

  return (
    <>
      {/* Guarantee Modal */}
      {renderGuaranteeModal()}

      {/* Header with Action Buttons */}
      <Box
        sx={{
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#667eea" }}>
            {getPageTitle()}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        {renderActionButtons()}
      </Box>

      {/* Main Form */}
      <Card sx={{ p: 3, background: "none", boxShadow: "none" }}>
        <Stack spacing={3}>
          {/* Name Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, width: "150px" }}
            >
              Name:
            </Typography>
            <TextField
              value={formData.clubName}
              onChange={(e) => handleInputChange("clubName", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  backgroundColor: "white",
                },
                width: "40%",
              }}
            />
          </Box>

          {/* Select Team Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, width: "150px" }}
            >
              Select Team:
            </Typography>
            <FormControl sx={{ width: "40%" }}>
              <Select
                value={formData.teamId}
                onChange={(e) => handleInputChange("teamId", e.target.value)}
                sx={{
                  borderRadius: 5,
                  backgroundColor: "white",
                  "& .MuiSelect-icon": {
                    color: "#667eea",
                  },
                }}
              >
                {teams.map((team) => (
                  <MenuItem key={team.scoreableId} value={team.externalRef}>
                    {team.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Min XP Level Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, width: "150px" }}
            >
              Min XP Level:
            </Typography>
            <FormControl sx={{ width: "40%" }}>
              <Select
                value={formData.minXpLevel}
                onChange={(e) =>
                  handleInputChange("minXpLevel", parseInt(e.target.value))
                }
                sx={{
                  borderRadius: 5,
                  backgroundColor: "white",
                  "& .MuiSelect-icon": {
                    color: "#667eea",
                  },
                }}
              >
                <MenuItem value={0}>Any</MenuItem>
                {xpLevels.map((level) => {
                  return (
                    <MenuItem key={level.levelId} value={level.levelId}>
                      Level {level.levelName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Entry Fee Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, width: "150px" }}
            >
              Coin entry:
            </Typography>
            <TextField
              value={formData.entryFee}
              onChange={(e) =>
                handleInputChange("entryFee", parseInt(e.target.value) || 0)
              }
              style={{ width: "40%" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 5,
                  backgroundColor: "white",
                },
              }}
              InputProps={{
                endAdornment: (
                  <img
                    src="/assets/icons/ufcoin.svg"
                    alt="dollar"
                    style={{ width: 20, height: 20, marginRight: 20 }}
                  />
                ),
              }}
            />
          </Box>

          {/* Payout Format Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, width: "150px" }}
            >
              Payout Format:
            </Typography>
            <FormControl sx={{ width: "40%" }}>
              <Select
                value={formData.payoutFormat || ""}
                onChange={(e) => handlePayoutFormatChange(e.target.value)}
                renderValue={renderPayoutFormat}
                sx={{
                  borderRadius: 5,
                  backgroundColor: "white",
                  "& .MuiSelect-icon": {
                    color: "#667eea",
                  },
                }}
              >
                {[
                  "2x",
                  "3x",
                  "4x",
                  "5x",
                  "10x",
                  "20x",
                  "WTA",
                  "Top2-80-20",
                  "Top2-70-30",
                  "Top3",
                  "50/50",
                  "GRT-WTA",
                  "GRT-Top3",
                  "GRT-Top5",
                  "GRT-Top10",
                ].map((option) => (
                  <MenuItem key={option} value={option}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <Chip
                        label={getChipLabel(option)}
                        size="small"
                        sx={{
                          backgroundColor: getChipColor(option),
                          color: "white",
                          fontWeight: 500,
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ flex: 1 }}
                      >
                        {getDescription(option)}
                      </Typography>
                      {option && option.startsWith("GRT-") && (
                        <Iconify
                          icon="eva:settings-2-outline"
                          sx={{
                            color: "#667eea",
                            width: 16,
                            height: 16,
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Prize Pool Percentage Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500, width: "150px" }}
            >
              Prize Pool:
            </Typography>
            <FormControl sx={{ width: "40%" }}>
              <Select
                value={formData.prizePoolPercentage}
                onChange={(e) =>
                  handleInputChange(
                    "prizePoolPercentage",
                    parseInt(e.target.value)
                  )
                }
                sx={{
                  borderRadius: 5,
                  backgroundColor: "white",
                  "& .MuiSelect-icon": {
                    color: "#667eea",
                  },
                }}
              >
                <MenuItem value={100}>100% (no commission)</MenuItem>
                <MenuItem value={95}>95%</MenuItem>
                <MenuItem value={92}>92%</MenuItem>
                <MenuItem value={90}>90%</MenuItem>
                <MenuItem value={85}>85%</MenuItem>
                <MenuItem value={80}>80%</MenuItem>
                <MenuItem value={20}>20%</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Max Users Field */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <MaxUsersSelect
              editmode={isEditMode}
              value={formData.maxUsers}
              onChange={(value) => handleInputChange("maxUsers", value)}
              labelWidth="150px"
              style={{ width: "400px" }}
            />

            <Box sx={{ ml: 4 }}>
              <MinUsersSelect
                value={formData.minUsers}
                onChange={(value) => handleInputChange("minUsers", value)}
                style={{ width: "400px" }}
                editmode={isEditMode}
              />
            </Box>
          </Box>

          {/* Image Upload Sections */}
          <Box sx={{ display: "flex", gap: 4, mt: 2, flexDirection: "column" }}>
            {/* Icon Upload */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, width: "150px" }}
              >
                Icon: (optional)
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: 212,
                    height: 210,
                    borderRadius: 2,
                    border: "2px dashed #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    overflow: "hidden",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  {formData.iconUrl ? (
                    <img
                      src={formData.iconUrl}
                      alt="icon 1"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Iconify
                      icon="eva:image-outline"
                      sx={{
                        color: "#ccc",
                        width: 100,
                        height: 100,
                      }}
                    />
                  )}
                </Box>
                {!isViewMode && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload("icon", e)}
                      style={{ display: "none" }}
                      id="icon-upload"
                    />
                    <label
                      style={{ textAlign: "center" }}
                      htmlFor="icon-upload"
                    >
                      <Typography
                        component="span"
                        sx={{
                          textAlign: "center",
                          color: "#667eea",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": {
                            color: "#5a6fd8",
                          },
                        }}
                      >
                        Upload
                      </Typography>
                    </label>
                  </>
                )}
              </Box>
            </Box>

            {/* Banner Upload */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, width: "150px" }}
              >
                Banner: (optional)
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    height: 180,
                    width: 660,
                    borderRadius: 2,
                    border: "2px dashed #ddd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1,
                    overflow: "hidden",
                    backgroundColor: "#f8f9fa",
                    position: "relative",
                  }}
                >
                  {formData.bannerUrl ? (
                    <img
                      src={formData.bannerUrl}
                      alt="Banner 1"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Iconify
                      icon="eva:image-outline"
                      sx={{ width: 120, height: 120, color: "#ccc" }}
                    />
                  )}
                </Box>
                {!isViewMode && (
                  <>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload("banner1", e)}
                      style={{ display: "none" }}
                      id="banner1-upload"
                    />
                    <label htmlFor="banner1-upload">
                      <Typography
                        component="span"
                        sx={{
                          color: "#667eea",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": {
                            color: "#5a6fd8",
                          },
                        }}
                      >
                        Upload
                      </Typography>
                    </label>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={formData.isAutoGeneration}
              onChange={() =>
                setFormData({
                  ...formData,
                  isAutoGeneration: !formData.isAutoGeneration,
                })
              }
              sx={{
                color: "#667eea",
                "&.Mui-checked": {
                  color: "#667eea",
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                cursor: "default",
              }}
            >
              Enable auto-generation
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block", ml: 5 }}
          >
            Challenge will run every day that Team is playing
          </Typography>
        </Stack>
      </Card>
    </>
  );
}
